// src/pages/Engagement.js — contacts sorted by derived health for retargeting
import { useState, useEffect, useMemo } from "react";
import { Avatar, HealthBadge, StageBadge, timeAgo } from "../components/UI";
import { watchContacts, compareContacts } from "../lib/db";
import AlertBanner from "../components/AlertBanner";
import EmptyState from "../components/EmptyState";
import { Heart } from "lucide-react";

const SEGMENTS = [
  { id: "all", label: "All (non-internal)", min: 0, max: 101 },
  { id: "hot", label: "High health (70+)", min: 70, max: 101 },
  { id: "warm", label: "Warm (40–69)", min: 40, max: 70 },
  { id: "cold", label: "At risk (0–39)", min: 0, max: 40 },
];

export default function Engagement({ onOpenContact }) {
  const [contacts, setContacts] = useState([]);
  const [segment, setSegment] = useState("hot");
  const [sortMode, setSortMode] = useState("health_desc");
  const [err, setErr] = useState(null);

  useEffect(() => {
    return watchContacts(
      (rows) => {
        setErr(null);
        setContacts(rows);
      },
      () => setErr("Could not load contacts.")
    );
  }, []);

  const filtered = useMemo(() => {
    const base = contacts.filter((c) => !c.isInternal);
    const seg = SEGMENTS.find((s) => s.id === segment) || SEGMENTS[0];
    return base.filter((c) => {
      const h = c.healthScore ?? 0;
      return h >= seg.min && h < seg.max;
    });
  }, [contacts, segment]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (sortMode === "health_desc") copy.sort((a, b) => (b.healthScore ?? 0) - (a.healthScore ?? 0));
    else if (sortMode === "health_asc") copy.sort((a, b) => (a.healthScore ?? 0) - (b.healthScore ?? 0));
    else if (sortMode === "active") copy.sort((a, b) => compareContacts(a, b, "last_active_desc"));
    else if (sortMode === "name") copy.sort((a, b) => compareContacts(a, b, "name_asc"));
    else copy.sort((a, b) => compareContacts(a, b, "health_desc"));
    return copy;
  }, [filtered, sortMode]);

  return (
    <div className="page-wrap" style={{ maxWidth: 1000 }}>
      <header className="page-header">
        <h1 className="page-header-title">Engagement & retargeting</h1>
        <p className="page-header-desc">
          Contacts sorted by <strong>health score</strong> (activity, scans, usage, payment issues, lifecycle). High health for upsell;
          low health for win-back.
        </p>
      </header>

      {err && <AlertBanner variant="error">{err}</AlertBanner>}

      <div className="filter-bar" style={{ marginBottom: 14, alignItems: "center" }}>
        {SEGMENTS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`filter-chip ${segment === s.id ? "active" : ""}`}
            onClick={() => setSegment(s.id)}
          >
            {s.label}
          </button>
        ))}
        <span style={{ opacity: 0.5 }}>|</span>
        <label style={{ fontSize: 12, color: "var(--text-3)", display: "inline-flex", alignItems: "center", gap: 8 }}>
          Sort
          <select className="input" value={sortMode} onChange={(e) => setSortMode(e.target.value)} style={{ marginBottom: 0, padding: "6px 10px", fontSize: 12 }}>
            <option value="health_desc">Health (high → low)</option>
            <option value="health_asc">Health (low → high)</option>
            <option value="active">Last active</option>
            <option value="name">Name (A–Z)</option>
          </select>
        </label>
        <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>
          {sorted.length} contact{sorted.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="card contacts-table-card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Contact</th>
                <th>Health</th>
                <th>Stage</th>
                <th>Last active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState icon={Heart} title="No contacts in this segment" description="Try another health band or check back when activity updates." />
                  </td>
                </tr>
              ) : (
                sorted.map((c) => (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => onOpenContact(c)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={c.name} size="sm" />
                        <div>
                          <div style={{ fontWeight: 500 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-3)" }}>{c.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <HealthBadge score={c.healthScore} />
                    </td>
                    <td>
                      <StageBadge stage={c.stage} />
                    </td>
                    <td style={{ fontSize: 12, color: "var(--text-2)" }}>{c.lastActiveAt ? timeAgo(c.lastActiveAt) : "—"}</td>
                    <td>
                      <button type="button" className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onOpenContact(c); }}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
